import { useState, useRef, useEffect } from 'react'

export type ViewFinderProps = {
  result: {
    resultPoints: { x: number; y: number }[]
  }
}

export const ViewFinder = (props: ViewFinderProps) => {
  const { result } = props
  const resultPoints = result?.resultPoints
  const [visible, setVisible] = useState(false)
  const [opacity, setOpacity] = useState(1)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [videoRect, setVideoRect] = useState<DOMRect | null>(null)

  // Find video element and update on resize
  useEffect(() => {
    const findVideoElement = () => {
      const video = document.querySelector('video')
      if (video) {
        videoRef.current = video
        setVideoRect(video.getBoundingClientRect())
      }
    }

    findVideoElement()
    const interval = setInterval(findVideoElement, 500)
    window.addEventListener('resize', findVideoElement)

    return () => {
      clearInterval(interval)
      window.removeEventListener('resize', findVideoElement)
    }
  }, [])

  // Handle QR code detection and fade animation
  useEffect(() => {
    if (!resultPoints?.length) return

    setVisible(true)
    setOpacity(1)

    const fadeTimer = setTimeout(() => {
      const fadeInterval = setInterval(() => {
        setOpacity((prev) => {
          if (prev <= 0.1) {
            clearInterval(fadeInterval)
            setVisible(false)
            return 0
          }
          return prev - 0.1
        })
      }, 50)

      return () => clearInterval(fadeInterval)
    }, 200)

    return () => clearTimeout(fadeTimer)
  }, [resultPoints])

  if (!visible || !resultPoints?.length || !videoRect) return null

  // Calculate bounding box dimensions
  const minX = Math.min(...resultPoints.map((p) => p.x))
  const minY = Math.min(...resultPoints.map((p) => p.y))
  const maxX = Math.max(...resultPoints.map((p) => p.x))
  const maxY = Math.max(...resultPoints.map((p) => p.y))

  const padding = 20
  const scaleX = videoRect.width / (videoRef.current?.videoWidth || 1)
  const scaleY = videoRect.height / (videoRef.current?.videoHeight || 1)

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: `${minY * scaleY - padding}px`,
          left: `${minX * scaleX - padding}px`,
          width: `${(maxX - minX) * scaleX + padding * 2}px`,
          height: `${(maxY - minY) * scaleY + padding * 2}px`,
          border: '3px solid rgba(76, 175, 80, 0.8)',
          borderRadius: '4px',
          boxShadow: '0 0 10px rgba(76, 175, 80, 0.5)',
          opacity,
          transition: 'opacity 0.05s linear',
        }}
      />
    </div>
  )
}
